import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Download, Lock } from 'lucide-react';
import bcrypt from 'bcryptjs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { decryptFile } from '../lib/encryption';

const SharedFilePage = () => {
  const { token } = useParams();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const fetchFile = async () => {
      const { data, error } = await supabase.from('files').select('*').eq('share_token', token).single();
      if (error || !data) { setExpired(true); setLoading(false); return; }
      if (new Date(data.share_expiry) < new Date()) { setExpired(true); setLoading(false); return; }
      setFile(data);
      setLoading(false);
    };
    fetchFile();
  }, [token]);

  const handleDownload = async () => {
    if (file.share_password && !bcrypt.compareSync(password, file.share_password)) {
      toast.error('Incorrect password');
      return;
    }
    setDownloading(true);
    try {
      const { data, error } = await supabase.storage.from('vault-files').download(file.storage_path);
      if (error) throw error;
      let blob;
      if (file.encryption_iv) {
        const encrypted = await data.arrayBuffer();
        const decrypted = await decryptFile(encrypted, file.encryption_iv);
        blob = new Blob([decrypted]);
      } else {
        blob = data;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.file_name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      const { error: downloadLogError } = await supabase.from('share_downloads').insert({
        file_id: file.id,
        file_name: file.file_name,
        share_token: token,
        downloaded_at: new Date().toISOString(),
      });

      if (downloadLogError) {
        console.warn('Share download tracking failed:', downloadLogError);
      }

      toast.success('File downloaded!');
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="font-mono text-muted-foreground">Loading...</p></div>;
  if (expired) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center"><Shield className="w-16 h-16 text-destructive mx-auto mb-4" /><h2 className="text-xl font-semibold mb-2">Link Expired or Invalid</h2><p className="text-sm text-muted-foreground">This share link is no longer valid.</p></div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
        <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Shared File</h2>
        <p className="text-sm text-muted-foreground mb-6">{file.file_name}</p>
        <p className="text-xs text-muted-foreground font-mono mb-4">Expires: {new Date(file.share_expiry).toLocaleString()}</p>

        {file.share_password && (
          <div className="mb-4 text-left">
            <Label className="flex items-center gap-1 mb-1"><Lock className="w-3 h-3" /> Password Required</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter share password" data-testid="shared-pw-input" />
          </div>
        )}

        <Button onClick={handleDownload} disabled={downloading} className="w-full" data-testid="shared-download-btn">
          <Download className="w-4 h-4 mr-2" />{downloading ? 'Downloading...' : 'Download File'}
        </Button>
      </div>
    </div>
  );
};

export default SharedFilePage;
